import { type SiteId } from '@solinkcorp/api-types';
import { useSite } from '@solinkcorp/callhome-data';
import { useAuthDevice } from '../contexts/DeviceContext';
import { usePeopleTrackingCamera } from '../contexts/PeopleTrackingContext';

/**
 * Shared site/device resolution + vision analytics config fetch, factored out of
 * `useSiteSpecificVisionAnalyticsCamera` so other consumers (e.g. the testing tab's real
 * zone/camera lookup) can read the same "line crossing configuration (with multicam)"
 * without duplicating the site → device → auth-location → config plumbing.
 *
 * `rawData` is keyed by VMS camera id (multicam shape): each entry has `event_lines` and
 * `dwell_event_polygons`, each carrying a `metadata.aiTagIds` association.
 */
export default function usePeopleTrackingRawData(siteId: SiteId | string | undefined) {
  const { site } = useSite({ siteId: siteId as SiteId });
  const device = site?.devices?.[0];

  const authLocation = useAuthDevice(device);

  const locationVersion = device?.currentSoftwareMajorVersion;
  const maxNumberOfCamerasOverride = device?.connectSettings?.ai?.line_crossing?.max_instances;
  const tpuStatus = device?.tpuStatus;

  const peopleTrackingCamera = usePeopleTrackingCamera({
    deviceUrl: authLocation?.accessibleAddress ?? '',
    locationCameras: site?.cameras,
    locationVersion,
    maxNumberOfCamerasOverride,
    tpuStatus,
    isVisionAnalyticsEnabled: device?.connectSettings?.ai?.line_crossing?.enabled,
  });

  return { rawData: peopleTrackingCamera.rawData };
}
